import { useId } from 'react';
import type { SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bell, ChevronRight, MessageCircle } from 'lucide-react';
import type { CompanionReport, ReportDevice, ReportFact } from '@/lib/companion';

export type ReportDevicesState = 'loading' | 'error' | 'ready';

function FactRow({ fact, onNavigate }: { fact: ReportFact; onNavigate?: () => void }) {
  const content = <>
    <span className="companion-fact-label">{fact.label}</span>
    <span className="companion-fact-value" data-tone={fact.tone}>{fact.value}</span>
  </>;
  if (!fact.href) return <div className="companion-fact">{content}</div>;
  return <Link to={fact.href} onClick={onNavigate} className="companion-fact" aria-label={`${fact.label}：${fact.value}`}>
    {content}<ChevronRight aria-hidden="true" size={14} className="companion-row-go" />
  </Link>;
}

function DeviceRow({ device, onNavigate }: { device: ReportDevice; onNavigate?: () => void }) {
  const spoken = [device.status, ...device.issues, ...device.meta].join('，');
  return <Link to={device.href} onClick={onNavigate} className="companion-device" aria-label={`${device.name}：${spoken}`}>
    <span className="min-w-0 flex-1">
      <span className="companion-device-name">{device.name}</span>
      <span className="companion-device-meta">
        <span className="companion-device-state" data-online={device.online}>{device.status}</span>
        {device.meta.map((item) => <span key={item}>{item}</span>)}
      </span>
    </span>
    {device.issues.length > 0 && <span className="companion-device-issues">
      {device.issues.map((issue) => <span key={issue} className="companion-issue">{issue}</span>)}
    </span>}
    <ChevronRight aria-hidden="true" size={14} className="companion-row-go" />
  </Link>;
}

/** 登入報告、手機卡片與小哨面板共用：先列整體，再列各裝置。 */
export function ReportBody({ report, devicesState, onNavigate }: {
  report: CompanionReport; devicesState: ReportDevicesState; onNavigate?: () => void;
}) {
  const id = useId();
  const showDevices = devicesState !== 'ready' || report.devices.length > 0;
  return <div className="companion-report">
    <section aria-labelledby={`${id}-overall`}>
      <h3 id={`${id}-overall`} className="companion-report-heading">整體</h3>
      <ul className="companion-rows">
        {report.facts.map((fact) => <li key={fact.id}><FactRow fact={fact} onNavigate={onNavigate} /></li>)}
      </ul>
    </section>
    {showDevices && <section aria-labelledby={`${id}-devices`} aria-busy={devicesState === 'loading' || undefined}>
      <h3 id={`${id}-devices`} className="companion-report-heading">裝置</h3>
      {devicesState === 'loading' ? <ReportSkeleton rows={2} />
        : devicesState === 'error' ? <p className="companion-report-note">暫時讀不到裝置清單，整體數字仍以總覽為準。</p>
          : <ul className="companion-rows">
            {report.devices.map((device) => <li key={device.id}><DeviceRow device={device} onNavigate={onNavigate} /></li>)}
          </ul>}
      {devicesState === 'ready' && report.moreDevices > 0 && <Link to="/devices" onClick={onNavigate} className="companion-text-link">
        還有 {report.moreDevices.toLocaleString('zh-TW')} 台<ArrowRight aria-hidden="true" size={14} />
      </Link>}
    </section>}
  </div>;
}

/** 資料還在路上時的佔位，形狀和報告的列相近，載入完不會整塊跳動。 */
export function ReportSkeleton({ rows = 4 }: { rows?: number }) {
  return <div className="companion-skeleton" aria-hidden="true">
    {Array.from({ length: rows }, (_, index) => <span key={index} className="skeleton" />)}
  </div>;
}

/** 報告底部的下一步：最需要處理的那一項、未讀通知、問小哨。 */
export function ReportActions({ action, unread, canChat, onNavigate, onNotifications, onAsk }: {
  action: { href: string; label: string } | null; unread: number; canChat: boolean;
  onNavigate: () => void; onNotifications: () => void; onAsk: (event: SyntheticEvent<HTMLElement>) => void;
}) {
  if (!action && unread === 0 && !canChat) return null;
  return <div className="companion-report-actions">
    {action && <Link to={action.href} onClick={onNavigate} className="companion-text-link">{action.label}<ArrowRight aria-hidden="true" size={14} /></Link>}
    {unread > 0 && <button type="button" className="companion-text-link" onClick={onNotifications}>看通知<Bell aria-hidden="true" size={14} /></button>}
    {canChat && <button type="button" className="companion-text-link text-muted-foreground" onClick={onAsk}>問小哨<MessageCircle aria-hidden="true" size={14} /></button>}
  </div>;
}
